import React, { useState, useEffect } from 'react';
import { BarChart, Bar, PieChart, Pie, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

const StatsView = ({
                       activeTab,
                       onLoadStats,
                       isLoadingStats,
                       statusCounts,
                       trendsByMonth
                   }) => {
    const [filters, setFilters] = useState({
        rangeType: 'LAST_1_MONTH',
        customStart: '',
        customEnd: ''
    });

    // Configuration Constants (Unchanged)
    const timeRangeOptions = [
        { value: 'LAST_1_WEEK', label: 'Last 1 Week' },
        { value: 'LAST_1_MONTH', label: 'Last 1 Month' },
        { value: 'LAST_4_MONTHS', label: 'Last 4 Months' },
        { value: 'LAST_6_MONTHS', label: 'Last 6 Months' },
        { value: 'LAST_1_YEAR', label: 'Last 1 Year' },
        { value: 'MORE_THAN_1_YEAR', label: 'More than 1 Year' },
        { value: 'CUSTOM_RANGE', label: 'Custom Date Range' }
    ];

    const COLORS = {
        VERIFIED: '#10b981',
        PENDING: '#f59e0b',
        FAILED: '#ef4444',
        primary: '#3b82f6',
        secondary: '#8b5cf6'
    };

    const getStatusColor = (status) => {
        const normalizedStatus = status?.toUpperCase();
        return COLORS[normalizedStatus] || COLORS.primary;
    };

    useEffect(() => {
        if (filters.rangeType !== 'CUSTOM_RANGE' || (filters.customStart && filters.customEnd)) {
            handleApplyFilters();
        }
    }, [activeTab]);

    // Auto-load when range type changes (except CUSTOM_RANGE without dates)
    useEffect(() => {
        if (filters.rangeType !== 'CUSTOM_RANGE') {
            handleApplyFilters();
        } else if (filters.rangeType === 'CUSTOM_RANGE' && filters.customStart && filters.customEnd) {
            handleApplyFilters();
        }
    }, [filters.rangeType]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));

        // If changing to a non-custom range, clear custom dates
        if (name === 'rangeType' && value !== 'CUSTOM_RANGE') {
            setFilters(prev => ({
                ...prev,
                [name]: value,
                customStart: '',
                customEnd: ''
            }));
        }
    };

    const handleApplyFilters = () => {
        const filterPayload = {
            rangeType: filters.rangeType,
            ...(filters.rangeType === 'CUSTOM_RANGE' && {
                customStart: filters.customStart ? `${filters.customStart}T00:00:00` : '',
                customEnd: filters.customEnd ? `${filters.customEnd}T23:59:59` : ''
            })
        };
        onLoadStats(filterPayload);
    };

    const handleResetFilters = () => {
        setFilters({
            rangeType: 'LAST_1_MONTH',
            customStart: '',
            customEnd: ''
        });
        onLoadStats({ rangeType: 'LAST_1_MONTH' });
    };

    // Calculate summary metrics
    const calculateSummary = () => {
        if (!statusCounts || statusCounts.length === 0) {
            return {
                totalVerifications: 0,
                verified: 0,
                pending: 0,
                failed: 0,
                successRate: 0
            };
        }

        const verified = statusCounts.find(s => s.verificationStatus?.toUpperCase() === 'VERIFIED')?.count || 0;
        const pending = statusCounts.find(s => s.verificationStatus?.toUpperCase() === 'PENDING')?.count || 0;
        const failed = statusCounts.find(s => s.verificationStatus?.toUpperCase() === 'FAILED')?.count || 0;
        const total = verified + pending + failed;
        const successRate = total > 0 ? (verified / total) * 100 : 0;
        return { totalVerifications: total, verified, pending, failed, successRate };
    };

    // Transform trends data for line chart
    const transformTrendsForLineChart = () => {
        if (!trendsByMonth || trendsByMonth.length === 0) return [];

        // Group by time period
        const grouped = {};
        trendsByMonth.forEach(item => {
            const period = item.timePeriod;
            if (!grouped[period]) {
                grouped[period] = { period: period, sortKey: item.sortKey, VERIFIED: 0, PENDING: 0, FAILED: 0, total: 0 };
            }
            const normalizedStatus = item.verificationStatus?.toUpperCase();
            grouped[period][normalizedStatus] = item.count;
            grouped[period].total += item.count;
        });

        // Convert to array and sort by sortKey
        return Object.values(grouped).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    };

    // Transform trends data for stacked bar chart
    const transformTrendsForBarChart = () => {
        return transformTrendsForLineChart();
    };

    const renderNiraStats = () => {
        const summary = calculateSummary();
        const lineChartData = transformTrendsForLineChart();
        const barChartData = transformTrendsForBarChart();

        return (
            <div className="statsview-content">
                {/* Summary Cards */}
                <div className="statsview-summary">
                    <div className="stat-card">
                        <div className="statsview-summary stat-icon" style={{ backgroundColor: COLORS.primary }}>
                            <i className="fas fa-check-circle"></i>
                        </div>
                        <div className="statsview-summary stat-details">
                            <h3>{summary.totalVerifications.toLocaleString()}</h3>
                            <p>Total Verifications</p>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="statsview-summary stat-icon" style={{ backgroundColor: COLORS.VERIFIED }}>
                            <i className="fas fa-percentage"></i>
                        </div>
                        <div className="statsview-summary stat-details">
                            <h3>{summary.successRate.toFixed(1)}%</h3>
                            <p>Success Rate</p>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="statsview-summary stat-icon" style={{ backgroundColor: COLORS.PENDING }}>
                            <i className="fas fa-clock"></i>
                        </div>
                        <div className="statsview-summary stat-details">
                            <h3>{summary.pending}</h3>
                            <p>Pending Verifications</p>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="statsview-summary stat-icon" style={{ backgroundColor: COLORS.FAILED }}>
                            <i className="fas fa-times-circle"></i>
                        </div>
                        <div className="statsview-summary stat-details">
                            <h3>{summary.failed}</h3>
                            <p>Failed Verifications</p>
                        </div>
                    </div>
                </div>

                {/* Charts Grid */}
                <div className="statsview-charts-grid">
                    {/* Status Distribution Pie Chart */}
                    <div className="statsview-chart-container">
                        <h3>Verification Status Distribution</h3>
                        {statusCounts && statusCounts.length > 0 ? (
                            <ResponsiveContainer width="100%" height={350}>
                                <PieChart>
                                    <Pie
                                        data={statusCounts}
                                        dataKey="count"
                                        nameKey="verificationStatus"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={80}
                                        innerRadius={40}
                                        label={({ verificationStatus, percent }) =>
                                            `${(percent * 100).toFixed(1)}%`
                                        }
                                        labelLine={false}
                                    >
                                        {statusCounts.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={getStatusColor(entry.verificationStatus)}
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        formatter={(value, name) => [`${value} verifications`, name]}
                                    />
                                    <Legend
                                        verticalAlign="bottom"
                                        height={36}
                                        formatter={(value, entry) => `${value}: ${entry.payload.count}`}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="statsview-no-data-chart">No data available</div>
                        )}
                    </div>

                    {/* Monthly Trends Bar Chart */}
                    <div className="statsview-chart-container">
                        <h3>Monthly Verification Volume</h3>
                        {barChartData && barChartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={350}>
                                <BarChart data={barChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="period" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="VERIFIED" stackId="a" fill={COLORS.VERIFIED} name="Verified" />
                                    <Bar dataKey="PENDING" stackId="a" fill={COLORS.PENDING} name="Pending" />
                                    <Bar dataKey="FAILED" stackId="a" fill={COLORS.FAILED} name="Failed" />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="statsview-no-data-chart">No data available</div>
                        )}
                    </div>

                    {/* Trends Line Chart */}
                    <div className="statsview-chart-container full-width">
                        <h3>Verification Trends Over Time</h3>
                        {lineChartData && lineChartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={350}>
                                <LineChart data={lineChartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="period" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey="total"
                                        stroke={COLORS.primary}
                                        strokeWidth={2}
                                        name="Total Verifications"
                                        dot={{ r: 4 }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="VERIFIED"
                                        stroke={COLORS.VERIFIED}
                                        strokeWidth={2}
                                        name="Verified"
                                        dot={{ r: 4 }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="FAILED"
                                        stroke={COLORS.FAILED}
                                        strokeWidth={2}
                                        name="Failed"
                                        dot={{ r: 4 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="statsview-no-data-chart">No data available</div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderStanbicStats = () => {
        return (
            <div className="statsview-content">
                <div className="statsview-coming-soon">
                    <i className="fas fa-chart-line"></i>
                    <h3>Stanbic Statistics</h3>
                    <p>Statistics for Stanbic integration will be available soon</p>
                </div>
            </div>
        );
    };

    const renderScbStats = () => {
        return (
            <div className="statsview-content">
                <div className="statsview-coming-soon">
                    <i className="fas fa-chart-bar"></i>
                    <h3>Standard Chartered Statistics</h3>
                    <p>Statistics for Standard Chartered integration will be available soon</p>
                </div>
            </div>
        );
    };

    // --- Main Component Render ---
    return (
        <div className="statsview-view">
            {/* Filter Section */}
            <div className="filter-section">
                <div className="filter-grid">
                    <div className="filter-group">
                        <label>Time Range</label>
                        <select
                            name="rangeType"
                            value={filters.rangeType}
                            onChange={handleFilterChange}
                        >
                            {timeRangeOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {filters.rangeType === 'CUSTOM_RANGE' && (
                        <>
                            <div className="filter-group">
                                <label>Start Date</label>
                                <input
                                    type="date"
                                    name="customStart"
                                    value={filters.customStart}
                                    onChange={handleFilterChange}
                                />
                            </div>
                            <div className="filter-group">
                                <label>End Date</label>
                                <input
                                    type="date"
                                    name="customEnd"
                                    value={filters.customEnd}
                                    onChange={handleFilterChange}
                                />
                            </div>
                        </>
                    )}
                </div>

                <div className="filter-actions">
                    <button
                        className="btn btn-primary"
                        onClick={handleApplyFilters}
                        disabled={isLoadingStats || (filters.rangeType === 'CUSTOM_RANGE' && (!filters.customStart || !filters.customEnd))}
                    >
                        <i className="fas fa-sync"></i>
                        {filters.rangeType === 'CUSTOM_RANGE' ? 'Load Statistics' : 'Refresh Statistics'}
                    </button>
                    <button className="btn btn-secondary" onClick={handleResetFilters} disabled={isLoadingStats}>
                        <i className="fas fa-redo"></i> Reset
                    </button>
                </div>
            </div>

            {/* Loading State */}
            {isLoadingStats ? (
                <div className="statsview-loading-overlay">
                    <div className="statsview-loading-overlay spinner"></div>
                    <p>Loading statistics...</p>
                </div>
            ) : (
                <>
                    {activeTab === 'nira' && renderNiraStats()}
                    {activeTab === 'stanbic' && renderStanbicStats()}
                    {activeTab === 'stanchart' && renderScbStats()}
                </>
            )}
        </div>
    );
};

export default StatsView;